// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import loadInvoicePaymentHistory from "@eisinc/App/Handlers/Data/InvoicePayment/LoadInvoicePaymentHistory";
import updateSearchFields from "@eisinc/App/Handlers/Pages/InvoicePaymentHistory/UpdateSearchFields";
import { CustomAppState, GetInvoicePaymentHistoryApiParameter } from "@eisinc/App/Store/Reducers";
import {
    getInvoicePaymentHistoryDataView,
    InvoicePaymentHistoryDataViewContext,
} from "@eisinc/App/Store/Selectors/InvoicePaymentHistorySelector";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import qs from "qs";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const getInvoicePaymentHistoryApiParameter =
        state.xInvoicePaymentHistoryListState.getInvoicePaymentHistoryApiParameter;

    return {
        getInvoicePaymentHistoryApiParameter,
        invoicePaymentHistoryDataView: getInvoicePaymentHistoryDataView(state, getInvoicePaymentHistoryApiParameter),
        location: getLocation(state),
    };
};

const mapDispatchToProps = {
    loadInvoicePaymentHistory,
    updateSearchFields,
};

type InvoicePaymentHistoryProps = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory;

const InvoicePaymentHistoryPage: React.FC<InvoicePaymentHistoryProps> = ({
    id,
    getInvoicePaymentHistoryApiParameter,
    invoicePaymentHistoryDataView,
    location,
    history,
    loadInvoicePaymentHistory,
    updateSearchFields,
}) => {
    let firstLoad = false;
    React.useEffect(() => {
        firstLoad = true;
        if (location.search) {
            const getInvoicesApiParameter = parseQueryString<GetInvoicePaymentHistoryApiParameter>(location.search);
            updateSearchFields({ ...getInvoicesApiParameter, type: "Replace" });
        }
    }, []);

    React.useEffect(() => {
        if (!firstLoad) {
            const queryString = qs.stringify(getInvoicePaymentHistoryApiParameter);
            history.replace(`${location.pathname}${queryString !== "" ? `?${queryString}` : ""}`);
        }
    }, [getInvoicePaymentHistoryApiParameter]);

    React.useEffect(() => {
        // if this is undefined it means someone changed the filters and we haven't loaded the new collection yet
        if (!invoicePaymentHistoryDataView.value && !invoicePaymentHistoryDataView.isLoading) {
            loadInvoicePaymentHistory(getInvoicePaymentHistoryApiParameter);
        }
    });

    return (
        <Page>
            <InvoicePaymentHistoryDataViewContext.Provider value={invoicePaymentHistoryDataView}>
                <Zone contentId={id} zoneName="Content"></Zone>
            </InvoicePaymentHistoryDataViewContext.Provider>
        </Page>
    );
};

const InvoicePaymentHistoryPageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(InvoicePaymentHistoryPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        fieldDefinitions: [],
        pageType: "System",
    },
};

export default InvoicePaymentHistoryPageModule;
